package com.daytimegaming.ourdailyfinances.presentation.home

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.isSystemInDarkTheme
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.automirrored.filled.KeyboardArrowRight
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TopAppBar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.tooling.preview.PreviewLightDark
import androidx.compose.ui.tooling.preview.PreviewParameter
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import com.daytimegaming.ourdailyfinances.presentation.preview.RecentSpendingPreviewParameterProvider
import com.daytimegaming.ourdailyfinances.presentation.theme.AppTheme
import com.daytimegaming.ourdailyfinances.presentation.theme.GlassCard
import com.daytimegaming.ourdailyfinances.presentation.util.formatAmount
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun RecentSpendingScreen(
    onNavigateBack: () -> Unit,
    onNavigateToCategorySpending: (String, SpendingRange) -> Unit,
    viewModel: RecentSpendingViewModel = koinViewModel()
) {
    val state by viewModel.state.collectAsStateWithLifecycle()

    LaunchedEffect(Unit) {
        viewModel.load()
    }

    RecentSpendingScreenContent(
        state = state,
        onNavigateBack = onNavigateBack,
        onRangeSelected = viewModel::setRange,
        onNavigateToCategorySpending = onNavigateToCategorySpending
    )
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun RecentSpendingScreenContent(
    state: RecentSpendingScreenState,
    onNavigateBack: () -> Unit,
    onRangeSelected: (SpendingRange) -> Unit,
    onNavigateToCategorySpending: (String, SpendingRange) -> Unit
) {
    Scaffold(
        modifier = Modifier.fillMaxSize(),
        topBar = {
            TopAppBar(
                title = {
                    Text(
                        text = "View All Recent Spending",
                        style = MaterialTheme.typography.titleMedium,
                        fontWeight = FontWeight.Bold
                    )
                },
                navigationIcon = {
                    IconButton(onClick = onNavigateBack) {
                        Icon(
                            imageVector = Icons.AutoMirrored.Filled.ArrowBack,
                            contentDescription = "Back"
                        )
                    }
                }
            )
        }
    ) { contentPadding ->
        Box(
            modifier = Modifier
                .fillMaxSize()
                .padding(contentPadding)
        ) {
            when (val currentState = state) {
                is RecentSpendingScreenState.Loading -> {
                    Box(
                        modifier = Modifier.fillMaxSize(),
                        contentAlignment = Alignment.Center
                    ) {
                        CircularProgressIndicator()
                    }
                }
                is RecentSpendingScreenState.Error -> {
                    Box(
                        modifier = Modifier.fillMaxSize(),
                        contentAlignment = Alignment.Center
                    ) {
                        Text(
                            text = currentState.message,
                            color = MaterialTheme.colorScheme.error,
                            style = MaterialTheme.typography.bodyMedium
                        )
                    }
                }
                is RecentSpendingScreenState.Loaded -> {
                    RecentSpendingContent(
                        state = currentState,
                        onRangeSelected = onRangeSelected,
                        onNavigateToCategorySpending = onNavigateToCategorySpending
                    )
                }
            }
        }
    }
}

@Composable
private fun RecentSpendingContent(
    state: RecentSpendingScreenState.Loaded,
    onRangeSelected: (SpendingRange) -> Unit,
    onNavigateToCategorySpending: (String, SpendingRange) -> Unit
) {
    val rangeText = when (state.selectedRange) {
        SpendingRange.SevenDays -> "Last 7 Days"
        SpendingRange.OneMonth -> "Last Month"
        SpendingRange.ThreeMonths -> "Last 3 Months"
        SpendingRange.SixMonths -> "Last 6 Months"
        SpendingRange.OneYear -> "Last Year"
        SpendingRange.YearToDate -> "Year to Date"
    }

    LazyColumn(
        modifier = Modifier.fillMaxSize(),
        contentPadding = PaddingValues(16.dp),
        verticalArrangement = Arrangement.spacedBy(12.dp)
    ) {
        item {
            Column(
                modifier = Modifier.fillMaxWidth(),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                val totalSpending = state.totalSpending
                val isNegative = totalSpending < 0
                val absSpending = kotlin.math.abs(totalSpending)
                val formattedSpending = absSpending.formatAmount()
                val parts = formattedSpending.split('.')
                val intPart = parts.getOrElse(0) { "0" }
                val decPart = parts.getOrNull(1) ?: "00"

                Text(
                    text = "Total Spent ($rangeText)".uppercase(),
                    style = MaterialTheme.typography.labelSmall.copy(letterSpacing = 2.sp),
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    fontWeight = FontWeight.Bold
                )
                Spacer(modifier = Modifier.height(8.dp))

                Row(
                    verticalAlignment = Alignment.Bottom
                ) {
                    if (isNegative) {
                        Text(
                            text = "-",
                            style = MaterialTheme.typography.titleLarge,
                            color = MaterialTheme.colorScheme.error,
                            modifier = Modifier.padding(bottom = 12.dp)
                        )
                    }
                    Text(
                        text = "$",
                        style = MaterialTheme.typography.titleLarge,
                        color = MaterialTheme.colorScheme.primary,
                        modifier = Modifier.padding(bottom = 12.dp)
                    )
                    Text(
                        text = intPart,
                        style = MaterialTheme.typography.displayLarge.copy(fontSize = 48.sp),
                        color = MaterialTheme.colorScheme.onSurface,
                        fontWeight = FontWeight.Bold
                    )
                    Text(
                        text = ".$decPart",
                        style = MaterialTheme.typography.titleLarge,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                        modifier = Modifier.padding(bottom = 8.dp)
                    )
                }

                Spacer(modifier = Modifier.height(8.dp))

                Row(
                    modifier = Modifier.fillMaxWidth().padding(bottom = 8.dp),
                    horizontalArrangement = Arrangement.Center,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    SegmentedControl(
                        selectedRange = state.selectedRange,
                        onRangeSelected = onRangeSelected
                    )
                }

                Spacer(modifier = Modifier.height(8.dp))
            }
        }

        if (state.categories.isEmpty()) {
            item {
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(vertical = 48.dp),
                    contentAlignment = Alignment.Center
                ) {
                    val emptyMessage = when (state.selectedRange) {
                        SpendingRange.SevenDays -> "No spending transactions recorded in the last 7 days."
                        SpendingRange.OneMonth -> "No spending transactions recorded in the last month."
                        SpendingRange.ThreeMonths -> "No spending transactions recorded in the last 3 months."
                        SpendingRange.SixMonths -> "No spending transactions recorded in the last 6 months."
                        SpendingRange.OneYear -> "No spending transactions recorded in the last year."
                        SpendingRange.YearToDate -> "No spending transactions recorded year to date."
                    }
                    Text(
                        text = emptyMessage,
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                }
            }
        } else {
            val sliceColors = listOf(
                Color(0xFF8B5CF6), // Purple
                Color(0xFF06B6D4), // Cyan
                Color(0xFFEC4899), // Pink
                Color(0xFF3B82F6), // Blue
                Color(0xFF10B981), // Emerald
                Color(0xFFF59E0B), // Amber
                Color(0xFFEF4444), // Red
                Color(0xFF6366F1), // Indigo
                Color(0xFF14B8A6), // Teal
                Color(0xFF84CC16)  // Lime
            )

            itemsIndexed(state.categories, key = { _, item -> item.category }) { index, breakdown ->
                val color = sliceColors.getOrElse(index % sliceColors.size) { Color.Gray }
                val percent = if (state.totalSpending > 0.0) {
                    (breakdown.amount / state.totalSpending) * 100.0
                } else {
                    0.0
                }

                GlassCard(
                    modifier = Modifier
                        .fillMaxWidth()
                        .clickable { onNavigateToCategorySpending(breakdown.category, state.selectedRange) }
                ) {
                    Row(
                        modifier = Modifier
                            .fillMaxWidth(),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.SpaceBetween
                    ) {
                        Row(
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.spacedBy(12.dp),
                            modifier = Modifier.weight(1f)
                        ) {
                            Box(
                                modifier = Modifier
                                    .size(16.dp)
                                    .background(color, CircleShape)
                            )
                            Column {
                                Text(
                                    text = breakdown.category,
                                    style = MaterialTheme.typography.bodyLarge,
                                    fontWeight = FontWeight.Bold,
                                    color = MaterialTheme.colorScheme.onSurface
                                )
                                Text(
                                    text = "${percent.formatPercent()}% of spending",
                                    style = MaterialTheme.typography.labelSmall,
                                    color = MaterialTheme.colorScheme.onSurfaceVariant
                                )
                            }
                        }

                        Row(
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.spacedBy(8.dp)
                        ) {
                            Text(
                                text = "$${breakdown.amount.formatAmount()}",
                                style = MaterialTheme.typography.titleMedium,
                                fontWeight = FontWeight.Bold,
                                color = MaterialTheme.colorScheme.onSurface
                            )
                            Icon(
                                imageVector = Icons.AutoMirrored.Filled.KeyboardArrowRight,
                                contentDescription = "View Details",
                                tint = MaterialTheme.colorScheme.onSurfaceVariant
                            )
                        }
                    }
                }
            }
        }
    }
}

private fun Double.formatPercent(): String {
    val formatted = this.formatAmount()
    return if (formatted.endsWith(".00")) {
        formatted.substringBefore(".00")
    } else {
        formatted
    }
}

@PreviewLightDark
@Composable
fun RecentSpendingScreenPreview(
    @PreviewParameter(RecentSpendingPreviewParameterProvider::class) state: RecentSpendingScreenState
) {
    AppTheme(
        darkTheme = isSystemInDarkTheme(),
        dynamicColor = false,
    ) {
        Surface {
            RecentSpendingScreenContent(
                state = state,
                onNavigateBack = {},
                onRangeSelected = {},
                onNavigateToCategorySpending = { _, _ -> }
            )
        }
    }
}
